import { Input } from "../../../../components/ui/input/SimpleInput";
import FormElement from "../../../../components/ui/formelement/FromElement";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import { Interview, InterviewMode, interviewModeNames } from "../../../../api/Interviews";
import SelectDropdownV2 from "../../../../components/ui/selectdropdown/v2";
import { TextareaV2 } from "../../../../components/ui/textbox/v2";
import { InterviewWithState } from "../../logic/InterviewAnalysisSlice";
import { formatDateForInput, getCurrentDate } from "../../../../lib/utils";
import ButtonV2 from "../../../../components/ui/button/v2";
import { Plus } from "lucide-react";
import { useState } from "react";
import CaseEvidenceModal from "./CaseEvidenceModal";

const INTERVIEWEE_TYPE_OPTIONS = ["Borrower", "Sourcing Channel", "Employee", "Related Party"]

interface InterviewFindingsFormProps {
    interviews: InterviewWithState[];
    selectedInterviewId: string | null;
    investigatorOptions: OptionNameValuePair<string>[];
    data: Interview | null;
    onChange: (key: keyof Interview, value: Interview[typeof key]) => void;
    disabled?: boolean;
}

const InterviewFindingsForm = (props: InterviewFindingsFormProps) => {
    const [showCaseEvidenceModal, setShowCaseEvidenceModal] = useState<boolean>(false);
    const interviewModeOptions: OptionNameValuePair<InterviewMode>[] = Object.values(InterviewMode).map((mode) => ({
        name: interviewModeNames[mode],
        value: mode
    }));

    const investigatorName = props.investigatorOptions.find((investigator: any) => (investigator.value === props.data?.investigator_email));

    return (
        <div className="flex flex-col w-full gap-4">
            <h2 className="text-sm font-semibold text-muted-color">Details</h2>
            <div className="">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-1">
                    <FormElement
                        labelClassName="font-medium text-xs text-muted-color"
                        name="Interviewee Name"
                        isMandatory={false}
                        inputComponent={
                            <Input
                                placeholder="Enter Name"
                                className="p-2 border border-input-border rounded-md"
                                value={props.data?.interviewee_name ?? ''}
                                onChange={(event) => props.onChange("interviewee_name", event.target.value)}
                                disabled={props.disabled}
                            />
                        }
                    />
                    <FormElement
                        labelClassName="font-medium text-xs text-muted-color"
                        name="Interviewee Type"
                        inputComponent={
                            <SelectDropdownV2
                                containerClassName="p-2 border border-input-border rounded-md"
                                focusClassName="outline outline-[3px] outline-[#C0C9FB] [outline-style:solid]"
                                name="Type"
                                placeholder="Select Type"
                                enableSearch={false}
                                disabled={props.disabled}
                                options={INTERVIEWEE_TYPE_OPTIONS.map((type) => ({
                                    name: type.replace("_", " "),
                                    value: type
                                }))}
                                onSelectionChange={(value) => props.onChange("interviewee_type", value.value)}
                                selectedOption={props.data ? {
                                    name: props.data.interviewee_type.replace("_", " "),
                                    value: props.data.interviewee_type
                                } : undefined}
                            />
                        }
                    />
                    <FormElement
                        labelClassName="font-medium text-xs text-muted-color"
                        name="Interview Date"
                        inputComponent={
                            <Input 
                                type="date" 
                                placeholder="Enter Date" 
                                className="p-[7px] border border-input-border rounded-md focus:outline focus:outline-[3px] focus:outline-[#C0C9FB] focus:[outline-style:solid]"
                                value={props.data ? props.data.date : formatDateForInput(getCurrentDate())}
                                onChange={(event) => props.onChange("date", event.target.value)}
                                disabled={props.disabled}
                            />
                        }
                    />
                    <FormElement
                        labelClassName="font-medium text-xs text-muted-color"
                        name="Mode"
                        inputComponent={
                            <SelectDropdownV2
                                containerClassName="p-2 border border-input-border rounded-md"
                                focusClassName="outline outline-[3px] outline-[#C0C9FB] [outline-style:solid]"
                                name="mode"
                                enableSearch={false}
                                placeholder="Select Mode"
                                disabled={props.disabled}
                                options={interviewModeOptions}
                                onSelectionChange={(value) => props.onChange("mode", value.value)}
                                selectedOption={props.data ? {
                                    name: interviewModeNames[props.data.mode],
                                    value: props.data.mode
                                } : undefined}
                            />
                        }
                    />
                    <FormElement
                        labelClassName="font-medium text-xs text-muted-color"
                        name="Investigator"
                        inputComponent={
                            <SelectDropdownV2
                                containerClassName="p-2 border border-input-border rounded-md"
                                focusClassName="outline outline-[3px] outline-[#C0C9FB] [outline-style:solid]"
                                name="investigator"
                                placeholder="Select Investigator"
                                disabled={props.disabled}
                                options={props.investigatorOptions}
                                onSelectionChange={(value) => props.onChange("investigator_email", value.value)}
                                selectedOption={props.data && investigatorName ? {
                                    name: investigatorName.name,
                                    value: investigatorName.value
                                } : undefined}
                            />
                        }
                    />
                    {/* Divider */}
                    <div className="border-t border-gray-200 col-span-5" />

                    <FormElement
                        name="Summary of the Interview"
                        labelClassName="font-medium text-xs text-muted-color"
                        className="col-span-5 mt-4 mb-2"
                        inputComponent={
                            <TextareaV2
                                className="w-full p-2 border border-input-border rounded-md min-h-[100px] resize-y focus:outline-[3px] focus:outline-[#C0C9FB] focus:shadow-none focus:[outline-offset:0px]"
                                placeholder="Enter summary..."
                                rows={10}
                                value={props.data ? props.data.findings : ''}
                                onChange={(event) => props.onChange("findings", event.target.value)}
                                disabled={props.disabled}
                            />
                        }
                    />
                    <FormElement
                        labelClassName="font-medium text-xs text-muted-color gap-x-1"
                        name="Evidence Links"
                        className="col-span-5"
                        isMandatory={false}
                        inputComponent={
                            <div className="flex gap-2 flex-wrap">
                                {props.data?.evidence_links.filter(Boolean).map((link, index) => (
                                    <div key={index} className="flex items-center gap-1 max-w-[180px] text-[#4B5563] rounded-md px-2 bg-[#EBEEFE]">
                                        <a
                                            key={index}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs truncate max-w-full block"
                                        >
                                            {link}
                                        </a>
                                        <div>
                                            <ButtonV2
                                                variant="ghost"
                                                size="sm"
                                                className="p-0 self-center"
                                                prefix={<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
                                                onClick={() => {
                                                    const updatedLinks = props.data?.evidence_links.filter((_, i) => i !== index);
                                                    props.onChange("evidence_links", updatedLinks);
                                                }}
                                                title="Remove Link"
                                                disabled={props.disabled}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <ButtonV2
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#3551F3] p-0"
                                    prefix={<Plus className="w-4 h-4"/>}
                                    onClick={() => setShowCaseEvidenceModal(true)}
                                    disabled={props.disabled}
                                >
                                    Add
                                </ButtonV2>
                            </div>
                        }
                    />
                </div>
            </div>
            {showCaseEvidenceModal && (
                <CaseEvidenceModal
                    isOpen={showCaseEvidenceModal}
                    onClose={() => setShowCaseEvidenceModal(false)}
                    onSave={(evidenceURL: string) => {
                        const updatedLinks = props.data?.evidence_links ? [...props.data.evidence_links, evidenceURL] : [evidenceURL];
                        props.onChange("evidence_links", updatedLinks);
                        setShowCaseEvidenceModal(false);
                    }}
                />
            )}
        </div>
    );
};

export default InterviewFindingsForm;